import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
    ArrowLeft,
    MessageCircle,
    Send,
    Clock,
    CheckCircle,
    AlertCircle,
    ChevronRight,
    Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { PrimaryButton, SecondaryButton, TertiaryButton, IconButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
    { id: 'general', label: 'General Question' },
    { id: 'billing', label: 'Billing & Subscription' },
    { id: 'technical', label: 'Technical Issue' },
    { id: 'feature_request', label: 'Feature Request' },
    { id: 'other', label: 'Other' }
];

const Support = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [view, setView] = useState('list'); // list, new, detail
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTicket, setSelectedTicket] = useState(null);
    
    // New ticket form
    const [newTicket, setNewTicket] = useState({
        subject: '',
        message: '',
        category: 'general'
    });
    const [submitting, setSubmitting] = useState(false);
    
    // Reply form
    const [replyMessage, setReplyMessage] = useState('');
    const [replying, setReplying] = useState(false);

    const fetchTickets = async () => {
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/support/tickets`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setTickets(data.tickets || []);
            }
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets();
    }, []);

    const handleSubmitTicket = async (e) => {
        e.preventDefault();
        if (!newTicket.subject.trim() || !newTicket.message.trim()) {
            toast.error('Please fill in all fields');
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/support/tickets`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newTicket)
            });

            if (response.ok) {
                const data = await response.json();
                toast.success('Ticket submitted successfully');
                setNewTicket({ subject: '', message: '', category: 'general' });
                setView('list');
                fetchTickets();
            } else {
                toast.error('Failed to submit ticket');
            }
        } catch (error) {
            toast.error('Failed to submit ticket');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyMessage.trim()) return;

        setReplying(true);
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/support/tickets/${selectedTicket.ticket_id}/reply`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ message: replyMessage })
            });

            if (response.ok) {
                toast.success('Reply sent');
                setReplyMessage('');
                // Refresh ticket
                const ticketRes = await fetch(`${API_URL}/api/support/tickets/${selectedTicket.ticket_id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (ticketRes.ok) {
                    setSelectedTicket(await ticketRes.json());
                }
                fetchTickets();
            } else {
                toast.error('Failed to send reply');
            }
        } catch (error) {
            toast.error('Failed to send reply');
        } finally {
            setReplying(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return 'bg-[#FFBB98] text-[#FFBB98]';
            case 'in_progress': return 'bg-[#7D8E95] text-[#7D8E95]';
            case 'resolved': return 'bg-[#7D8E95]/30 text-[#1D6B6B]';
            case 'closed': return 'bg-[#FFBB98] text-[#7D8E95]';
            default: return 'bg-[#FFBB98] text-[#7D8E95]';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'open': return <AlertCircle className="w-4 h-4" />;
            case 'in_progress': return <Clock className="w-4 h-4" />;
            case 'resolved': return <CheckCircle className="w-4 h-4" />;
            default: return <MessageCircle className="w-4 h-4" />;
        }
    };

    // Ticket Detail View
    if (view === 'detail' && selectedTicket) {
        return (
            <div className="min-h-screen bg-transparent">
                {/* Header */}
                <header className="glass-header sticky top-0 z-10">
                    <Container size="md" className="py-4 flex items-center gap-4">
                        <IconButton onClick={() => { setView('list'); setSelectedTicket(null); }}>
                            <ArrowLeft className="w-5 h-5" />
                        </IconButton>
                        <div className="flex-1">
                            <Heading as="h1" size="text-lg">Ticket #{selectedTicket.ticket_id}</Heading>
                            <SmallText className="text-[#7D8E95]">{selectedTicket.subject}</SmallText>
                        </div>
                        <span className={`px-3 py-1 text-xs rounded-full ${getStatusColor(selectedTicket.status)}`}>
                            {selectedTicket.status.replace('_', ' ')}
                        </span>
                    </Container>
                </header>

                <main className="max-w-2xl mx-auto px-4 py-6">
                    {/* Messages */}
                    <div className="space-y-4 mb-6">
                        {selectedTicket.messages?.map((msg, idx) => (
                            <div 
                                key={msg.id || idx}
                                className={`p-4 rounded-2xl ${
                                    msg.sender === 'admin' 
                                        ? 'bg-[#7D8E95]/20 border border-[#7D8E95]/30 ml-8' 
                                        : 'bg-white border border-white/40 mr-8'
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <span className={`text-xs font-medium ${
                                        msg.sender === 'admin' ? 'text-[#1D6B6B]' : 'text-[#7D8E95]'
                                    }`}>
                                        {msg.sender === 'admin' ? (msg.sender_name || 'Support Team') : 'You'}
                                    </span>
                                    <span className="text-xs text-[#7D8E95]">
                                        {new Date(msg.timestamp).toLocaleString()}
                                    </span>
                                </div>
                                <BodyText className="text-[#1D6B6B] whitespace-pre-wrap">{msg.message}</BodyText>
                            </div>
                        ))}
                    </div>

                    {/* Reply Form */}
                    {selectedTicket.status !== 'closed' && (
                        <form onSubmit={handleReply} className="bg-white rounded-2xl border border-white/40 p-4">
                            <textarea
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                placeholder="Type your reply..."
                                className="w-full p-3 border border-white/40 rounded-xl resize-none focus:outline-none focus:border-[#1D6B6B]"
                                rows={3}
                            />
                            <div className="flex justify-end mt-3">
                                <PrimaryButton type="submit" disabled={replying || !replyMessage.trim()}>
                                    <Send className="w-4 h-4 mr-2" />
                                    {replying ? 'Sending...' : 'Send Reply'}
                                </PrimaryButton>
                            </div>
                        </form>
                    )}
                </main>
            </div>
        );
    }

    // New Ticket Form
    if (view === 'new') {
        return (
            <div className="min-h-screen bg-transparent">
                <header className="glass-header sticky top-0 z-10">
                    <Container size="md" className="py-4 flex items-center gap-4">
                        <IconButton onClick={() => setView('list')}>
                            <ArrowLeft className="w-5 h-5" />
                        </IconButton>
                        <Heading as="h1" size="text-xl">New Support Ticket</Heading>
                    </Container>
                </header>

                <main className="max-w-xl mx-auto px-4 py-6">
                    <form onSubmit={handleSubmitTicket} className="space-y-6">
                        {/* Category */}
                        <div>
                            <label className="text-sm text-[#7D8E95] mb-2 block">Category</label>
                            <div className="flex flex-wrap gap-2">
                                {CATEGORIES.map(cat => (
                                    <button
                                        key={cat.id}
                                        type="button"
                                        onClick={() => setNewTicket({...newTicket, category: cat.id})}
                                        className={`px-4 py-2 rounded-full text-sm transition-colors ${
                                            newTicket.category === cat.id
                                                ? 'bg-[#1D6B6B] text-white'
                                                : 'bg-white border border-white/40 text-[#7D8E95] hover:border-[#1D6B6B]'
                                        }`}
                                    >
                                        {cat.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Subject */}
                        <div>
                            <label className="text-sm text-[#7D8E95] mb-2 block">Subject</label>
                            <input
                                type="text"
                                value={newTicket.subject}
                                onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                                placeholder="Brief description of your issue"
                                className="w-full p-3 border border-white/40 rounded-xl focus:outline-none focus:border-[#1D6B6B]"
                                required
                            />
                        </div>

                        {/* Message */}
                        <div>
                            <label className="text-sm text-[#7D8E95] mb-2 block">Message</label>
                            <textarea
                                value={newTicket.message}
                                onChange={(e) => setNewTicket({...newTicket, message: e.target.value})}
                                placeholder="Please describe your issue in detail..."
                                className="w-full p-3 border border-white/40 rounded-xl resize-none focus:outline-none focus:border-[#1D6B6B]"
                                rows={6}
                                required
                            />
                        </div>

                        <PrimaryButton type="submit" disabled={submitting} className="w-full">
                            {submitting ? 'Submitting...' : 'Submit Ticket'}
                        </PrimaryButton>
                    </form>
                </main>
            </div>
        );
    }

    // Ticket List
    return (
        <div className="min-h-screen bg-transparent">
            <header className="glass-header sticky top-0 z-10">
                <Container size="md" className="py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <IconButton onClick={() => navigate('/settings')}>
                            <ArrowLeft className="w-5 h-5" />
                        </IconButton>
                        <Heading as="h1" size="text-xl">Support</Heading>
                    </div>
                    <PrimaryButton onClick={() => setView('new')} size="sm">
                        <Plus className="w-4 h-4 mr-1" />
                        New Ticket
                    </PrimaryButton>
                </Container>
            </header>

            <main className="max-w-2xl mx-auto px-4 py-6">
                {/* Direct Contact Info */}
                <div className="bg-white/70 rounded-xl p-4 mb-6 border border-white/40/30">
                    <SmallText className="text-[#7D8E95] mb-3 block">
                        <strong className="text-[#1D6B6B]">Prefer email?</strong> Reach us directly:
                    </SmallText>
                    <div className="space-y-2">
                        <a 
                            href="mailto:support@coregulateadhd.com"
                            className="flex items-center gap-2 text-[#1D6B6B] hover:underline text-sm"
                        >
                            <span className="font-medium">support@coregulateadhd.com</span>
                            <span className="text-[#7D8E95]">— Billing, technical issues, problems</span>
                        </a>
                        <a 
                            href="mailto:hello@coregulateadhd.com"
                            className="flex items-center gap-2 text-[#1D6B6B] hover:underline text-sm"
                        >
                            <span className="font-medium">hello@coregulateadhd.com</span>
                            <span className="text-[#7D8E95]">— General enquiries, feedback</span>
                        </a>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-12">
                        <div className="w-3 h-3 rounded-full bg-[#1D6B6B] animate-pulse mx-auto mb-4" />
                        <SmallText className="text-[#7D8E95]">Loading tickets...</SmallText>
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="text-center py-12">
                        <IconContainer variant="sage" size="xl" useGradient={true} className="mx-auto mb-4">
                            <MessageCircle className="w-8 h-8" />
                        </IconContainer>
                        <Heading as="h2" size="text-lg" className="mb-2">No tickets yet</Heading>
                        <BodyText className="text-[#7D8E95] mb-6">
                            Need help? Create a support ticket and we'll get back to you.
                        </BodyText>
                        <PrimaryButton onClick={() => setView('new')}>
                            Create Your First Ticket
                        </PrimaryButton>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {tickets.map(ticket => (
                            <button
                                key={ticket.ticket_id}
                                onClick={() => { setSelectedTicket(ticket); setView('detail'); }}
                                className="w-full bg-white rounded-xl border border-white/40 p-4 text-left hover:border-[#1D6B6B]/50 transition-colors"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xs text-[#7D8E95]">#{ticket.ticket_id}</span>
                                            <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(ticket.status)}`}>
                                                {ticket.status.replace('_', ' ')}
                                            </span>
                                        </div>
                                        <BodyText className="font-medium text-[#1D6B6B] truncate">{ticket.subject}</BodyText>
                                        <SmallText className="text-[#7D8E95]">
                                            {ticket.messages?.length || 0} message{ticket.messages?.length !== 1 ? 's' : ''} • 
                                            Updated {new Date(ticket.updated_at).toLocaleDateString()}
                                        </SmallText>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default Support;
